import React, { useState, useCallback, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ComponentPermissionSet,
  ComponentPermissionType,
  PropertyPermissionSet,
  ElementPermissionSet,
  PermissionRule,
  SystemRole,
  ProjectRole,
  ComponentRole,
  PermissionCondition,
  ConditionType,
  ConditionOperator,
  PermissionSource,
  ValidationRule,
} from "@/types/permissions";
import { Component, ComponentType } from "@/types";
import { useComponentPermissions } from "@/contexts/PermissionContext";
import { useAuthStore } from "@/stores/auth/auth-store";
import { config } from "@/config";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Shield,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Edit,
  Trash,
  Copy,
  Move,
  Maximize2,
  Palette,
  Type,
  Layout,
  Zap,
  MessageSquare,
  PenTool,
  Share,
  Download,
  Upload,
  GitBranch,
  AlertTriangle,
  Info,
  Check,
  X,
  ChevronRight,
  ChevronDown,
  User,
  Users,
  Building,
  Globe,
} from "lucide-react";
import { cn } from "@/lib/utils";

// Permission type icons
const PERMISSION_ICONS: Record<
  ComponentPermissionType,
  React.FC<{ className?: string }>
> = {
  [ComponentPermissionType.CREATE]: Upload,
  [ComponentPermissionType.READ]: Eye,
  [ComponentPermissionType.UPDATE]: Edit,
  [ComponentPermissionType.DELETE]: Trash,
  [ComponentPermissionType.CLONE]: Copy,
  [ComponentPermissionType.MOVE]: Move,
  [ComponentPermissionType.RESIZE]: Maximize2,
  [ComponentPermissionType.STYLE]: Palette,
  [ComponentPermissionType.EDIT_CONTENT]: Type,
  [ComponentPermissionType.EDIT_LAYOUT]: Layout,
  [ComponentPermissionType.EDIT_BEHAVIOR]: Zap,
  [ComponentPermissionType.COMMENT]: MessageSquare,
  [ComponentPermissionType.ANNOTATE]: PenTool,
  [ComponentPermissionType.SHARE]: Share,
  [ComponentPermissionType.EXPORT]: Download,
  [ComponentPermissionType.PUBLISH]: Upload,
  [ComponentPermissionType.VERSION]: GitBranch,
};

// Permission descriptions
const PERMISSION_DESCRIPTIONS: Record<ComponentPermissionType, string> = {
  [ComponentPermissionType.CREATE]: "Create new components",
  [ComponentPermissionType.READ]: "View component content",
  [ComponentPermissionType.UPDATE]: "Modify component properties",
  [ComponentPermissionType.DELETE]: "Remove components",
  [ComponentPermissionType.CLONE]: "Duplicate components",
  [ComponentPermissionType.MOVE]: "Change component position",
  [ComponentPermissionType.RESIZE]: "Adjust component dimensions",
  [ComponentPermissionType.STYLE]: "Modify visual styling",
  [ComponentPermissionType.EDIT_CONTENT]: "Edit text and content",
  [ComponentPermissionType.EDIT_LAYOUT]: "Modify layout structure",
  [ComponentPermissionType.EDIT_BEHAVIOR]: "Configure interactions",
  [ComponentPermissionType.COMMENT]: "Add comments",
  [ComponentPermissionType.ANNOTATE]: "Create annotations",
  [ComponentPermissionType.SHARE]: "Share with others",
  [ComponentPermissionType.EXPORT]: "Export component",
  [ComponentPermissionType.PUBLISH]: "Publish changes",
  [ComponentPermissionType.VERSION]: "Manage versions",
};

interface ComponentPermissionPanelProps {
  component: Component;
  onPermissionChange?: (permissions: ComponentPermissionSet) => void;
  readOnly?: boolean;
}

export const ComponentPermissionPanel: React.FC<
  ComponentPermissionPanelProps
> = ({ component, onPermissionChange, readOnly = false }) => {
  const { tokens } = useAuthStore();
  const queryClient = useQueryClient();
  const permissions = useComponentPermissions(component.id);

  // State
  const [localPermissions, setLocalPermissions] =
    useState<ComponentPermissionSet | null>(null);
  const [propertyPermissions, setPropertyPermissions] = useState<
    PropertyPermissionSet[]
  >([]);
  const [elementPermissions, setElementPermissions] = useState<
    ElementPermissionSet[]
  >([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [selectedPermissionType, setSelectedPermissionType] =
    useState<ComponentPermissionType | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(["basic"]),
  );

  // Initialize local permissions
  useEffect(() => {
    if (permissions && !localPermissions) {
      setLocalPermissions(permissions);
    }
  }, [permissions, localPermissions]);

  // Update component permissions mutation
  const updatePermissionsMutation = useMutation({
    mutationFn: async (updates: Partial<ComponentPermissionSet>) => {
      const response = await fetch(
        `${config.api.baseUrl}/components/${component.id}/permissions`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${tokens?.accessToken}`,
          },
          body: JSON.stringify(updates),
        },
      );
      if (!response.ok) throw new Error("Failed to update permissions");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["permissions", component.id],
      });
    },
  });

  // Toggle section expansion
  const toggleSection = (section: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  // Update single permission
  const updatePermission = useCallback(
    (type: ComponentPermissionType, updates: Partial<PermissionRule>) => {
      if (!localPermissions || readOnly) return;

      const updatedPermissions = {
        ...localPermissions,
        [type]: {
          ...localPermissions[type],
          ...updates,
        },
      };

      setLocalPermissions(updatedPermissions);

      // Debounced save
      updatePermissionsMutation.mutate({ [type]: updatedPermissions[type] });

      if (onPermissionChange) {
        onPermissionChange(updatedPermissions);
      }
    },
    [localPermissions, readOnly, updatePermissionsMutation, onPermissionChange],
  );

  // Add role to permission
  const addRoleToPermission = (
    type: ComponentPermissionType,
    role: string,
    roleType: "system" | "project" | "component",
  ) => {
    if (!localPermissions) return;

    const permission = localPermissions[type];
    const allowedRoles = [...permission.allowedRoles];

    if (!allowedRoles.includes(role as any)) {
      allowedRoles.push(role as any);
      updatePermission(type, { allowedRoles });
    }
  };

  // Remove role from permission
  const removeRoleFromPermission = (
    type: ComponentPermissionType,
    role: string,
  ) => {
    if (!localPermissions) return;

    const permission = localPermissions[type];
    const allowedRoles = permission.allowedRoles.filter((r) => r !== role);
    updatePermission(type, { allowedRoles });
  };

  // Add user to permission
  const addUserToPermission = (
    type: ComponentPermissionType,
    userId: string,
    allowed: boolean,
  ) => {
    if (!localPermissions) return;

    const permission = localPermissions[type];

    if (allowed) {
      const allowedUsers = [...permission.allowedUsers];
      const deniedUsers = permission.deniedUsers.filter((id) => id !== userId);

      if (!allowedUsers.includes(userId)) {
        allowedUsers.push(userId);
      }

      updatePermission(type, { allowedUsers, deniedUsers });
    } else {
      const deniedUsers = [...permission.deniedUsers];
      const allowedUsers = permission.allowedUsers.filter(
        (id) => id !== userId,
      );

      if (!deniedUsers.includes(userId)) {
        deniedUsers.push(userId);
      }

      updatePermission(type, { allowedUsers, deniedUsers });
    }
  };

  // Permission group component
  const PermissionGroup = ({
    title,
    permissions: permTypes,
    icon: Icon,
  }: {
    title: string;
    permissions: ComponentPermissionType[];
    icon: React.FC<{ className?: string }>;
  }) => {
    const sectionKey = title.toLowerCase();
    const isExpanded = expandedSections.has(sectionKey);

    return (
      <div className="border rounded-lg">
        <button
          className="w-full flex items-center justify-between p-3 hover:bg-accent/50 transition-colors"
          onClick={() => toggleSection(sectionKey)}
        >
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{title}</span>
            <Badge variant="secondary" className="text-xs">
              {permTypes.length}
            </Badge>
          </div>
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>

        {isExpanded && (
          <div className="p-3 pt-0 space-y-2">
            {permTypes.map((type) => {
              const permission = localPermissions?.[type];
              if (!permission) return null;

              const PermIcon = PERMISSION_ICONS[type];
              const isSelected = selectedPermissionType === type;

              return (
                <div
                  key={type}
                  className={cn(
                    "rounded-lg border p-3 transition-all cursor-pointer",
                    isSelected && "ring-2 ring-primary bg-accent/30",
                    !permission.allowed && "opacity-60",
                  )}
                  onClick={() =>
                    setSelectedPermissionType(isSelected ? null : type)
                  }
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <div
                        className={cn(
                          "p-1.5 rounded-md",
                          permission.allowed
                            ? "bg-green-100 dark:bg-green-900/30"
                            : "bg-red-100 dark:bg-red-900/30",
                        )}
                      >
                        <PermIcon
                          className={cn(
                            "h-4 w-4",
                            permission.allowed
                              ? "text-green-600 dark:text-green-400"
                              : "text-red-600 dark:text-red-400",
                          )}
                        />
                      </div>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">
                            {type
                              .split(":")[1]
                              .replace(/_/g, " ")
                              .replace(/\b\w/g, (l) => l.toUpperCase())}
                          </span>
                          {permission.inherited && (
                            <Badge variant="outline" className="text-xs">
                              Inherited
                            </Badge>
                          )}
                          {permission.conditions &&
                            permission.conditions.length > 0 && (
                              <Badge variant="secondary" className="text-xs">
                                Conditional
                              </Badge>
                            )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {PERMISSION_DESCRIPTIONS[type]}
                        </p>
                      </div>
                    </div>

                    <Switch
                      checked={permission.allowed}
                      onCheckedChange={(checked) =>
                        updatePermission(type, { allowed: checked })
                      }
                      disabled={readOnly || permission.inherited}
                    />
                  </div>

                  {isSelected && (
                    <div className="mt-3 pt-3 border-t space-y-3">
                      {/* Allowed Roles */}
                      <div>
                        <Label className="text-xs">Allowed Roles</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {permission.allowedRoles.map((role) => (
                            <Badge
                              key={role}
                              variant="secondary"
                              className="text-xs"
                            >
                              {role}
                              {!readOnly && !permission.inherited && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeRoleFromPermission(type, role);
                                  }}
                                  className="ml-1"
                                >
                                  <X className="h-2 w-2" />
                                </button>
                              )}
                            </Badge>
                          ))}
                          {permission.allowedRoles.length === 0 && (
                            <span className="text-xs text-muted-foreground">
                              No roles
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Specific Users */}
                      {permission.allowedUsers.length > 0 && (
                        <div>
                          <Label className="text-xs">Allowed Users</Label>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {permission.allowedUsers.map((userId) => (
                              <Badge
                                key={userId}
                                variant="outline"
                                className="text-xs"
                              >
                                <User className="h-2 w-2 mr-1" />
                                {userId}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {permission.deniedUsers.length > 0 && (
                        <div>
                          <Label className="text-xs">Denied Users</Label>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {permission.deniedUsers.map((userId) => (
                              <Badge
                                key={userId}
                                variant="destructive"
                                className="text-xs"
                              >
                                <User className="h-2 w-2 mr-1" />
                                {userId}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Source */}
                      {permission.source && (
                        <div className="text-xs text-muted-foreground">
                          Source: {permission.source}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (!localPermissions) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center text-muted-foreground">
          Loading permissions...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Component Info */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-base">Component Permissions</CardTitle>
              <CardDescription className="text-xs mt-1">
                {component.name} ({component.type})
              </CardDescription>
            </div>
            <Badge variant="outline">ID: {component.id.slice(0, 8)}...</Badge>
          </div>
        </CardHeader>
      </Card>

      {/* Permission Groups */}
      <div className="space-y-3">
        <PermissionGroup
          title="Basic Operations"
          permissions={[
            ComponentPermissionType.CREATE,
            ComponentPermissionType.READ,
            ComponentPermissionType.UPDATE,
            ComponentPermissionType.DELETE,
          ]}
          icon={Shield}
        />

        <PermissionGroup
          title="Advanced Operations"
          permissions={[
            ComponentPermissionType.CLONE,
            ComponentPermissionType.MOVE,
            ComponentPermissionType.RESIZE,
            ComponentPermissionType.STYLE,
          ]}
          icon={Edit}
        />

        <PermissionGroup
          title="Content Management"
          permissions={[
            ComponentPermissionType.EDIT_CONTENT,
            ComponentPermissionType.EDIT_LAYOUT,
            ComponentPermissionType.EDIT_BEHAVIOR,
          ]}
          icon={Layout}
        />

        <PermissionGroup
          title="Collaboration"
          permissions={[
            ComponentPermissionType.COMMENT,
            ComponentPermissionType.ANNOTATE,
            ComponentPermissionType.SHARE,
          ]}
          icon={Users}
        />

        <PermissionGroup
          title="Publishing"
          permissions={[
            ComponentPermissionType.EXPORT,
            ComponentPermissionType.PUBLISH,
            ComponentPermissionType.VERSION,
          ]}
          icon={Upload}
        />
      </div>

      {/* Advanced Settings */}
      <Card>
        <CardHeader
          className="cursor-pointer"
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Advanced Settings</CardTitle>
            {showAdvanced ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </div>
        </CardHeader>

        {showAdvanced && (
          <CardContent>
            <Tabs defaultValue="properties" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="properties">Properties</TabsTrigger>
                <TabsTrigger value="elements">Elements</TabsTrigger>
                <TabsTrigger value="inheritance">Inheritance</TabsTrigger>
              </TabsList>

              <TabsContent value="properties" className="mt-4">
                <div className="space-y-3">
                  <div className="text-sm text-muted-foreground">
                    Configure property-level permissions for fine-grained
                    control.
                  </div>
                  {component.properties &&
                    Object.entries(component.properties).map(([key, value]) => (
                      <div
                        key={key}
                        className="flex items-center justify-between p-2 border rounded"
                      >
                        <div>
                          <span className="font-medium text-sm">{key}</span>
                          <p className="text-xs text-muted-foreground">
                            Type: {typeof value}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                >
                                  <Eye className="h-3 w-3" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">Read</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                >
                                  <Edit className="h-3 w-3" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">Write</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                      </div>
                    ))}
                </div>
              </TabsContent>

              <TabsContent value="elements" className="mt-4">
                <div className="space-y-3">
                  <div className="text-sm text-muted-foreground">
                    Control visibility and interaction permissions for child
                    elements.
                  </div>
                  {component.children && component.children.length > 0 ? (
                    component.children.map((childId) => (
                      <div
                        key={childId}
                        className="flex items-center justify-between p-2 border rounded"
                      >
                        <span className="text-sm">
                          Child: {childId.slice(0, 8)}...
                        </span>
                        <div className="flex items-center gap-2">
                          <Switch size="sm" />
                          <span className="text-xs">Visible</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-sm text-muted-foreground">
                      No child elements
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="inheritance" className="mt-4">
                <div className="space-y-3">
                  <Alert>
                    <Info className="h-4 w-4" />
                    <AlertDescription className="text-xs">
                      This component inherits permissions from its parent
                      container and project settings.
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 border rounded">
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">Project Permissions</span>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        Active
                      </Badge>
                    </div>

                    {component.parentId && (
                      <div className="flex items-center justify-between p-2 border rounded">
                        <div className="flex items-center gap-2">
                          <Building className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">Parent Container</span>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          Inherited
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        )}
      </Card>

      {/* Save indicator */}
      {updatePermissionsMutation.isPending && (
        <div className="fixed bottom-4 right-4 bg-background border rounded-lg shadow-lg p-3">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
            <span className="text-sm">Saving permissions...</span>
          </div>
        </div>
      )}

      {updatePermissionsMutation.isSuccess && (
        <div className="fixed bottom-4 right-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg shadow-lg p-3">
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
            <span className="text-sm text-green-800 dark:text-green-200">
              Permissions saved
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
